import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/ApiService';
import Navigation from '../components/Navigation';
import Avatar from '../components/Avatar';
import LocationSelector from '../components/LocationSelector';
import PromoBanner from '../components/PromoBanner';
import HashtagSelector from '../components/HashtagSelector';
import ShareableProfileCard from '../components/ShareableProfileCard';
import './Profile.css';

interface Variant {
  id: number;
  name: string;
  description?: string;
}

interface Hashtag {
  id: number;
  name: string;
  displayName: string;
  category: string | null;
  isOfficial: boolean;
  userCount: number;
}

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState('');
  const [age, setAge] = useState('');
  const [location, setLocation] = useState('');
  const [interests, setInterests] = useState('');
  const [variantId, setVariantId] = useState('');
  const [maxDistance, setMaxDistance] = useState('50');
  const [variants, setVariants] = useState<Variant[]>([]);
  const [userHashtags, setUserHashtags] = useState<Hashtag[]>([]);
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);

  useEffect(() => {
    fetchVariants();
    fetchUserHashtags();
    if (user?.profile) {
      setBio(user.profile.bio || '');
      setAge(user.profile.age?.toString() || '');
      setLocation(user.profile.location || '');
      setInterests(user.profile.interests || '');
      setVariantId(user.profile.variantId?.toString() || '');
      setMaxDistance(user.profile.maxDistance?.toString() || '50');
    } else {
      setEditing(true);
    }
  }, [user]);

  const fetchVariants = async () => {
    try {
      const response = await ApiService.get('/variants');
      setVariants(response.data);
    } catch (error) {
      console.error('Failed to fetch variants:', error);
    }
  };

  const fetchUserHashtags = async () => {
    try {
      const response = await ApiService.get('/hashtags/my-hashtags');
      setUserHashtags(response.data.hashtags);
    } catch (error) {
      console.error('Failed to fetch hashtags:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage('Please select an image file');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedFile) return;

    setUploadingPhoto(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);

      await ApiService.post('/upload/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await refreshUser();
      setSelectedFile(null);
      setMessage('Photo uploaded successfully!');
    } catch (error) {
      setMessage('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      await ApiService.delete('/upload/profile-picture');
      await refreshUser();
      setMessage('Photo deleted successfully!');
    } catch (error) {
      setMessage('Failed to delete photo');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    try {
      await ApiService.post('/profile', {
        bio,
        age: age ? parseInt(age) : null,
        location,
        interests,
        variantId: variantId ? parseInt(variantId) : null,
        maxDistance: maxDistance ? parseInt(maxDistance) : 50,
      });

      await refreshUser();
      setEditing(false);
      setMessage('Profile updated successfully!');
    } catch (error) {
      setMessage('Failed to update profile');
    }
  };

  return (
    <>
      <Navigation />
      <div className="profile-container">
        <div className="profile-box">
          <PromoBanner />
          <h1>Your Profile</h1>

          {message && (
            <div className={message.includes('success') ? 'success-message' : 'error-message'}>
              {message}
            </div>
          )}

          {!editing && user?.profile ? (
            <div className="profile-view">
              <div className="profile-photo-section">
                <Avatar
                  photoUrl={user.profile.photoUrl}
                  name={user.name}
                  size="large"
                />
                <div className="photo-actions">
                  <label htmlFor="photo-upload" className="upload-label">
                    {user.profile.photoUrl ? 'Change Photo' : 'Add Photo'}
                  </label>
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  {user.profile.photoUrl && (
                    <button onClick={handlePhotoDelete} className="delete-photo-btn">
                      Delete Photo
                    </button>
                  )}
                </div>
                {selectedFile && (
                  <div className="file-preview">
                    <p>Selected: {selectedFile.name}</p>
                    <button
                      onClick={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      className="upload-btn"
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                )}
              </div>

              <div className="profile-field">
                <label>Name:</label>
                <p>{user.name || 'Not set'}</p>
              </div>

              <div className="profile-field">
                <label>Email:</label>
                <p>{user.email}</p>
              </div>

              {user.profile.variant && (
                <div className="profile-field">
                  <label>Personality Type:</label>
                  <p>
                    {user.profile.variant.name}
                    {user.profile.variant.description && (
                      <span className="variant-desc"> - {user.profile.variant.description}</span>
                    )}
                  </p>
                </div>
              )}

              {user.profile.age && (
                <div className="profile-field">
                  <label>Age:</label>
                  <p>{user.profile.age}</p>
                </div>
              )}

              {user.profile.location && (
                <div className="profile-field">
                  <label>Location:</label>
                  <p>{user.profile.location}</p>
                </div>
              )}

              {user.profile.bio && (
                <div className="profile-field">
                  <label>Bio:</label>
                  <p>{user.profile.bio}</p>
                </div>
              )}

              {user.profile.interests && (
                <div className="profile-field">
                  <label>Interests:</label>
                  <p>{user.profile.interests}</p>
                </div>
              )}

              <div className="profile-field">
                <label>Maximum Distance for Matches:</label>
                <p>{user.profile.maxDistance || 50} miles</p>
              </div>

              {/* Display User Hashtags */}
              {userHashtags.length > 0 && (
                <div className="profile-field">
                  <label>Hashtags:</label>
                  <div className="hashtag-display">
                    {userHashtags.map(hashtag => (
                      <span key={hashtag.id} className="hashtag-badge">
                        {hashtag.displayName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="button-group">
                <button onClick={() => setEditing(true)} className="edit-button">
                  Edit Profile
                </button>
                <button onClick={() => setShowShareCard(true)} className="share-button">
                  📤 Share Profile
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="profile-photo-section">
                <Avatar
                  photoUrl={user?.profile?.photoUrl}
                  name={user?.name}
                  size="large"
                />
                <div className="photo-actions">
                  <label htmlFor="photo-upload-edit" className="upload-label">
                    {user?.profile?.photoUrl ? 'Change Photo' : 'Add Photo'}
                  </label>
                  <input
                    id="photo-upload-edit"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  {user?.profile?.photoUrl && (
                    <button type="button" onClick={handlePhotoDelete} className="delete-photo-btn">
                      Delete Photo
                    </button>
                  )}
                </div>
                {selectedFile && (
                  <div className="file-preview">
                    <p>Selected: {selectedFile.name}</p>
                    <button
                      type="button"
                      onClick={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      className="upload-btn"
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="variant">Choose Your Personality Type</label>
                <select
                  id="variant"
                  value={variantId}
                  onChange={(e) => setVariantId(e.target.value)}
                  required
                >
                  <option value="">Select your personality type...</option>
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name}
                      {variant.description && ` - ${variant.description}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="age">Age</label>
                <input
                  id="age"
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="Enter your age"
                  min="18"
                  max="999"
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <LocationSelector
                  value={location}
                  onChange={setLocation}
                  placeholder="Enter your location or click 📍 to detect"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bio">Bio</label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell others about yourself..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="interests">Interests</label>
                <input
                  id="interests"
                  type="text"
                  value={interests}
                  onChange={(e) => setInterests(e.target.value)}
                  placeholder="e.g., Mischief, Time Travel, Magic"
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxDistance">
                  Maximum Distance for Matches: <strong>{maxDistance || 50} miles</strong>
                </label>
                <input
                  id="maxDistance"
                  type="range"
                  min="1"
                  max="500"
                  value={maxDistance}
                  onChange={(e) => setMaxDistance(e.target.value)}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666' }}>
                  <span>1 mile</span>
                  <span>500 miles</span>
                </div>
              </div>

              {/* Hashtag Selector */}
              <div className="hashtag-section" style={{ marginTop: '2rem' }}>
                <HashtagSelector
                  selectedHashtags={userHashtags}
                  onHashtagsChange={(hashtags) => {
                    setUserHashtags(hashtags);
                    fetchUserHashtags();
                  }}
                  maxHashtags={10}
                />
              </div>

              <div className="button-group">
                <button type="submit" className="save-button">
                  Save Profile
                </button>
                {user?.profile && (
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {showShareCard && (
        <ShareableProfileCard onClose={() => setShowShareCard(false)} />
      )}
    </>
  );
};

export default Profile;
